
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import { GroupService } from './group.service';
import dayjs from 'dayjs';
import { ToastFactory } from '@/utils/ToastFactory';
const groupService = new GroupService();

const WEEK_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export default defineComponent({
  name: 'ListGroups',
  data() {
    return {
      groups: [] as any[],

      month: new Date(),

      calendarVisible: false,

      selectedEvent: null as any,

      selectedGroupDetail: null as any,

      loadingGroup: false as boolean,

      params:{ dateFinish:'', dateInit: '', page: 0, size: 100 },
    }
  },
  computed:{
    ...mapGetters({
      storeGroups: 'groups/getGroups',
      storeGroup: 'groups/getGroup',
    }),

    selectedGroup(): any {
      if(this.selectedGroupDetail) return this.selectedGroupDetail;

      return this.selectedEvent ? this.groupOf(this.selectedEvent.id) : null;
    },

    selectedGroupInfo(): any[] {
      return this.selectedGroup ? this.groupInfo(this.selectedGroup, this.selectedEvent) : [];
    },

    alocations(): any[] {
      const events: any[] = [];

      this.groups.forEach((group: any) => {
        const calendar = group.calendarList && group.calendarList[0];
        if(!calendar) return;

        const start = dayjs(group.createdDate);
        const finish = dayjs(group.finishDate);
        if(!start.isValid() || !finish.isValid()) return;

        const duration = finish.diff(start, 'days');
        const daysWeek = (calendar.daysWeek || []).map((day: any) => day.day);

        const occurrences: any[] = [];

        for(let i = 0; i <= duration; i++){
          const date = start.add(i, 'days');

          if(!daysWeek.includes(date.day())) continue;

          const day = date.format('YYYY-MM-DD');

          occurrences.push({
            title: group.name,
            time: {
              start: `${day} ${this.formatHour(calendar.checkIn)}`,
              end: `${day} ${this.formatHour(calendar.checkOut)}`,
            },
            isEditable: true,
            disableDnD: ['month', 'week', 'day'],
            id: group.id,
          });
        }

        const range = `${start.format('DD/MM')} - ${finish.format('DD/MM')}`;
        occurrences.forEach((occurrence: any, index: number) => {
          occurrence.rangeLabel = range;
          occurrence.rangePosition = occurrences.length === 1
            ? 'single'
            : index === 0 ? 'start' : index === occurrences.length - 1 ? 'end' : 'middle';
        });

        if(!occurrences.length){
          for(let i = 0; i <= duration; i++){
            const day = start.add(i, 'days').format('YYYY-MM-DD');

            events.push({
              title: group.name,
              time: { start: day, end: day },
              isEditable: false,
              disableDnD: ['month', 'week', 'day'],
              id: group.id,
              colorScheme: 'period',
              rangeLabel: range,
              rangePosition: duration === 0
                ? 'single'
                : i === 0 ? 'start' : i === duration ? 'end' : 'middle',
            });
          }
        }

        events.push(...occurrences);
      });

      return events;
    },
  },

  methods: {
    selectEvent(event: any){
      this.selectedEvent = event;
      this.selectedGroupDetail = null;
      this.loadingGroup = true;

      groupService.getGroupById(event.id).then(() => {
        if(this.selectedEvent && this.selectedEvent.id !== event.id) return;

        this.selectedGroupDetail = this.storeGroup;
        this.loadingGroup = false;
      }).catch(() => {
        if(!this.selectedEvent || this.selectedEvent.id === event.id) this.loadingGroup = false;
      });
    },

    openEvent(event: any){
      this.$router.push({ name: 'editGroup', params: { id: event } });
    },

    formatDate(date: string){
      return date ? dayjs(date).format('DD/MM/YYYY') : '-';
    },

    formatHour(hour: string){
      return hour ? hour.slice(0, 5) : '';
    },

    hours(group: any){
      const calendar = group.calendarList && group.calendarList[0];
      if(!calendar) return '-';

      return `${this.formatHour(calendar.checkIn)} - ${this.formatHour(calendar.checkOut)}`;
    },

    groupOf(id: string){
      return this.groups.find((group: any) => group.id === id) || null;
    },

    groupInfo(group: any, event: any){
      const eventStart = event && event.time ? event.time.start : '';

      const info = [
        {
          icon: 'event',
          label: this.$t('group.fields.date'),
          value: this.formatDate(eventStart.slice(0, 10)),
        },
        {
          icon: 'schedule',
          label: this.$t('group.fields.hours'),
          value: this.hours(group),
        },
        {
          icon: 'date_range',
          label: this.$t('group.fields.period'),
          value: `${this.formatDate(group.createdDate)} - ${this.formatDate(group.finishDate)}`,
        },
        {
          icon: 'repeat',
          label: this.$t('group.fields.weekDays'),
          value: this.weekDays(group),
        },
      ];

      if(group.organization){
        info.push({
          icon: 'apartment',
          label: this.$t('group.fields.organization'),
          value: group.organization.name,
        });
      }

      if(group.computers && group.computers.length){
        info.push({
          icon: 'computer',
          label: this.$t('group.equipament'),
          value: group.computers.map((computer: any) => computer.name).join(', '),
        });
      }

      return info;
    },

    eventTooltip(event: any){
      const group = this.groupOf(event.id);
      if(!group) return event.title;

      return [
        group.name,
        `Status: ${this.statusLabel(group.status)}`,
        ...this.groupInfo(group, event).map((info: any) => `${info.label}: ${info.value}`),
      ].join('\n');
    },

    weekDays(group: any){
      const calendar = group.calendarList && group.calendarList[0];
      if(!calendar || !calendar.daysWeek || !calendar.daysWeek.length) return '-';

      return calendar.daysWeek
        .map((day: any) => day.day)
        .sort((a: number, b: number) => a - b)
        .map((day: number) => this.$t(`group.week.${WEEK_KEYS[day]}`))
        .join(', ');
    },

    statusLabel(status: string){
      if(status === 'ACTIVE') return this.$t('group.fields.active');
      if(status === 'INACTIVATED') return this.$t('group.fields.inactive');
      if(status === 'RESERVED') return this.$t('group.fields.reserved');
      return status;
    },

    deleteGroup(id: any){
      groupService.deleteGroup(id).then(() => {
        this.getGroups();
        this.$toast.add(ToastFactory.success(this.$t("toast.sucessGroupDelete")));
      }).catch(() => {
        this.$toast.add(ToastFactory.error(this.$t("toast.errorGroupDelete")));
      })

    },

    confirmDelete(id: any){
      const group = this.groupOf(id);

      this.$confirm.require({
        header: this.$t('group.delete'),
        icon: 'pi pi-exclamation-triangle',
        message: group
          ? this.$t('group.confirmDeleteNamed', { name: group.name })
          : this.$t('group.confirmDelete'),
        accept: () => {
          this.deleteGroup(id);
        },
      });
    },

    getGroups(){
      groupService.getGroups(this.params).then(() => {
        this.groups = Array.isArray(this.storeGroups) ? this.storeGroups : (this.storeGroups?.content || []);
      });
    },

    applyRange(start: string, end: string){
      if(this.params.dateInit === start && this.params.dateFinish === end) return;

      this.params.dateInit = start;
      this.params.dateFinish = end;
      this.getGroups();
    },

    selectMonth(date?: Date){
      const month = dayjs(date || this.month);

      this.applyRange(
        month.startOf('month').format('YYYY-MM-DD'),
        month.endOf('month').format('YYYY-MM-DD')
      );
    },

    getRange(event: any){
      if(!event || !event.start || !event.end) return;

      const start = dayjs(event.start);
      const end = dayjs(event.end);

      this.month = start.add(end.diff(start, 'days') / 2, 'days').toDate();
      this.selectMonth(this.month);
    },

  },

  mounted() {
    this.selectMonth();
  }

})
